import { UserInfoFormValues } from "@/components/HolderForm/UserInfoFormValues.ts";
import { WarningToast } from "@/components/Notifications/WarningToast.tsx";
import { PaymentFormData } from "@/components/Payment/PaymentForm/interfaces/PaymentFormData.ts";
import { PaymentFormValues } from "@/components/Payment/PaymentForm/interfaces/PaymentFormValues.ts";
import { HolderFormData } from "@/pages/ReservationHolderInformation/HolderFormData.ts";
import { getFieldRefs } from "@/stores/FieldRefsStore/FieldRefsStore.ts";
import { useCallback } from "react";
import { FieldErrors } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export interface HolderStepValidatedData {
  holder: UserInfoFormValues;
  payment?: PaymentFormValues;
}

const scrollToFirstFormError = (errorFieldNames: string[], fieldEls: Map<string, HTMLElement>) => {
  let topmost: { el: HTMLElement; top: number } | null = null;

  for (const name of errorFieldNames) {
    const el = fieldEls.get(name);
    if (!el) continue;
    const top = el.getBoundingClientRect().top;
    if (!topmost || top < topmost.top) topmost = { el, top };
  }

  if (!topmost) return;

  topmost.el.scrollIntoView({ behavior: "smooth", block: "center" });

  const focusable =
    topmost.el.querySelector<HTMLElement>("input:not([type='hidden'])") ??
    topmost.el.querySelector<HTMLElement>("textarea") ??
    topmost.el.querySelector<HTMLElement>("button");
  focusable?.focus({ preventScroll: true });
};

/**
 * Validates the step-3 holder form (and payment form when enabled), returning
 * the validated data on success or null on failure. On failure, surfaces a
 * toast and scrolls/focuses the topmost erroring field.
 *
 * Flow:
 *   1. FloatingInput / CountrySelect / PhoneInput / PaymentForm card+expiry
 *      register their wrapper divs into FieldRefsStore via useRegisterFieldRef
 *      (keyed by RHF field name).
 *   2. On click of Continue (sidebar / mobile bar / Stepper next-step), this
 *      hook runs both forms via RHF's handleSubmit(onValid, onInvalid). The
 *      onValid callbacks capture validated data; onInvalid callbacks capture
 *      FieldErrors. We never re-validate downstream — the data returned here
 *      is what gets committed.
 *   3. If any errors: toast.custom(<WarningToast>) + scrollToFirstFormError,
 *      which looks up each error's field name in FieldRefsStore, picks the
 *      DOM-topmost wrapper by getBoundingClientRect, scrolls it into view
 *      and focuses its first focusable child.
 *
 * NOTE: both forms have shouldFocusError: false because RHF's auto-focus
 * crashes on react-input-mask Controller refs in the payment form; we focus
 * manually via the field-refs registry instead.
 */
export const useTryHolderFormSubmit = (holderFormData: HolderFormData, paymentFormData: PaymentFormData) => {
  const { t } = useTranslation();
  const { handleSubmit: handleSubmitHolder } = holderFormData;
  const { handleSubmitPaymentForm, formEnabled: paymentFormEnabled } = paymentFormData;

  return useCallback(async (): Promise<HolderStepValidatedData | null> => {
    let holderData: UserInfoFormValues | null = null;
    let paymentData: PaymentFormValues | undefined;
    let holderErrors: FieldErrors | null = null;
    let paymentErrors: FieldErrors | null = null;

    await handleSubmitHolder(
      (data) => {
        holderData = data;
      },
      (errs) => {
        holderErrors = errs;
      },
    )();

    if (paymentFormEnabled) {
      await handleSubmitPaymentForm(
        (data) => {
          paymentData = data;
        },
        (errs) => {
          paymentErrors = errs;
        },
      )();
    }

    if (holderErrors || paymentErrors) {
      const errorNames = [
        ...(holderErrors ? Object.keys(holderErrors) : []),
        ...(paymentErrors ? Object.keys(paymentErrors) : []),
      ];
      toast.custom(<WarningToast message={t("form.fillRequiredFields")} />);
      scrollToFirstFormError(errorNames, getFieldRefs());
      return null;
    }

    return { holder: holderData!, payment: paymentData };
  }, [handleSubmitHolder, handleSubmitPaymentForm, paymentFormEnabled, t]);
};
